import React, { useState, useCallback } from 'react';
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { walkingTime } from '../utils/distance';
import { getPhotoUrl } from '../utils/format';

const CAMPUS_LNG = -91.5302;
const CAMPUS_LAT = 41.6611;

// ── Pin ───────────────────────────────────────────────────────────────────────
function Pin({ color, active }) {
  return (
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: '50% 50% 50% 0',
        background: color,
        border: '2.5px solid white',
        boxShadow: active
          ? `0 0 0 3px ${color}55, 0 4px 14px rgba(0,0,0,0.28)`
          : '0 2px 8px rgba(0,0,0,0.22)',
        transform: 'rotate(-45deg)',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease',
      }}
    />
  );
}

// ── Popup card ────────────────────────────────────────────────────────────────
function ListingPopup({ listing, pinType, onClose, onView }) {
  const effectiveType = listing._listingType || pinType;
  const accentColor = effectiveType === 'sublease' ? '#0D9488' : '#C8A800';
  const btnTextColor = effectiveType === 'sublease' ? '#FFFFFF' : '#1B1B1B';

  const price = listing.monthly_rent != null
    ? `$${Number(listing.monthly_rent).toLocaleString()}/mo`
    : listing.rent_amount != null
    ? `$${Number(listing.rent_amount).toLocaleString()}/mo`
    : null;

  const beds = listing.bedrooms != null ? `${listing.bedrooms} bed` : null;
  const title = listing.property_name || listing.address || 'Listing';
  const photo = getPhotoUrl(listing.photos?.[0] || listing.photo_url || null);
  const lat = parseFloat(listing.latitude);
  const lng = parseFloat(listing.longitude);
  const walk = walkingTime(lat, lng);

  return (
    <Popup
      longitude={lng}
      latitude={lat}
      anchor="bottom"
      offset={16}
      closeButton
      closeOnClick={false}
      onClose={onClose}
      maxWidth="240px"
    >
      <div style={{ width: 210, fontFamily: 'system-ui, sans-serif' }}>
        {photo ? (
          <img
            src={photo}
            alt={title}
            style={{ width: 'calc(100% + 24px)', height: 110, objectFit: 'cover', display: 'block', margin: '-12px -12px 10px' }}
          />
        ) : (
          <div style={{ width: 'calc(100% + 24px)', height: 80, background: '#F4F4F4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '-12px -12px 10px' }}>
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#CCCCCC" strokeWidth="1.5">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
        )}

        <p style={{ fontWeight: 700, fontSize: 13, color: '#1B1B1B', margin: '0 0 6px', lineHeight: 1.3 }}>{title}</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
          {price && <span style={{ fontWeight: 700, color: accentColor, fontSize: 13 }}>{price}</span>}
          {beds && <span style={{ fontSize: 11, color: '#777', background: '#F4F4F4', padding: '2px 6px', borderRadius: 99 }}>{beds}</span>}
          {walk && <span style={{ fontSize: 11, color: '#555', background: '#F0FDF4', padding: '2px 6px', borderRadius: 99 }}>🚶 {walk}</span>}
        </div>

        <button
          onClick={() => onView(listing)}
          style={{
            width: '100%', padding: '8px 0',
            background: accentColor, color: btnTextColor,
            border: 'none', borderRadius: 7,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          View Listing
        </button>
      </div>
    </Popup>
  );
}

function getPinColor(listing, pinType) {
  const type = listing._listingType || pinType;
  return type === 'sublease' ? '#0D9488' : '#C8A800';
}

// ── MapView ───────────────────────────────────────────────────────────────────
export default function MapView({ listings = [], pinType = 'apartment', onViewListing }) {
  const [selectedId, setSelectedId] = useState(null);
  const pinColor = pinType === 'sublease' ? '#0D9488' : '#C8A800';

  const handleView = useCallback((listing) => {
    if (onViewListing) onViewListing(listing);
  }, [onViewListing]);

  const validListings = listings.filter(l => {
    const lat = parseFloat(l.latitude);
    const lng = parseFloat(l.longitude);
    return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  });

  const selectedListing = validListings.find(l => l.id === selectedId) || null;

  return (
    <div style={{ position: 'relative', width: '100%', height: 580, borderRadius: 16, overflow: 'hidden', border: '1px solid #EBEBEB' }}>
      <Map
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        initialViewState={{
          longitude: CAMPUS_LNG,
          latitude: CAMPUS_LAT,
          zoom: 14,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        onClick={() => setSelectedId(null)}
      >
        <NavigationControl position="top-right" />

        {/* Campus centre dot */}
        <Marker longitude={CAMPUS_LNG} latitude={CAMPUS_LAT} anchor="center">
          <div style={{
            width: 14, height: 14, borderRadius: '50%',
            background: '#FFCD00', border: '2.5px solid #B89700',
            boxShadow: '0 0 0 4px rgba(200,168,0,0.2)',
          }} />
        </Marker>

        {/* Listing pins */}
        {validListings.map(listing => {
          const lat = parseFloat(listing.latitude);
          const lng = parseFloat(listing.longitude);
          const color = getPinColor(listing, pinType);
          return (
            <Marker
              key={listing.id}
              longitude={lng}
              latitude={lat}
              anchor="bottom"
              onClick={e => {
                e.originalEvent.stopPropagation();
                setSelectedId(listing.id);
              }}
            >
              <Pin color={color} active={selectedId === listing.id} />
            </Marker>
          );
        })}

        {/* Selected popup */}
        {selectedListing && (
          <ListingPopup
            listing={selectedListing}
            pinType={selectedListing._listingType || pinType}
            onClose={() => setSelectedId(null)}
            onView={handleView}
          />
        )}
      </Map>

      {/* Legend */}
      <div style={{
        position: 'absolute', bottom: 12, left: 12,
        background: 'white', borderRadius: 8, padding: '6px 10px',
        fontSize: 11, color: '#555', fontFamily: 'system-ui',
        boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
        display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'none',
      }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFCD00', display: 'inline-block', border: '1.5px solid #B89700' }} />
        <span>University of Iowa</span>
        <span style={{ width: 1, height: 10, background: '#E0E0E0', display: 'inline-block' }} />
        <span style={{ width: 10, height: 10, borderRadius: '50% 50% 50% 0', background: '#C8A800', display: 'inline-block', transform: 'rotate(-45deg)', border: '1.5px solid white' }} />
        <span>Apartments</span>
        {pinType === 'mixed' && (
          <>
            <span style={{ width: 1, height: 10, background: '#E0E0E0', display: 'inline-block' }} />
            <span style={{ width: 10, height: 10, borderRadius: '50% 50% 50% 0', background: '#0D9488', display: 'inline-block', transform: 'rotate(-45deg)', border: '1.5px solid white' }} />
            <span>Subleases</span>
          </>
        )}
        {pinType === 'sublease' && (
          <>
            <span style={{ width: 10, height: 10, borderRadius: '50% 50% 50% 0', background: '#0D9488', display: 'inline-block', transform: 'rotate(-45deg)', border: '1.5px solid white' }} />
            <span>Subleases</span>
          </>
        )}
      </div>

    </div>
  );
}
